import React from "react";
import { useNavigate } from "react-router-dom";
import { Download, Printer, Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import {
  BackLink,
  HeroStatus,
  Section,
  SectionLabel,
  EntityCard,
  StatCard,
  StatsRow,
  DetailList,
  FooterActions,
  ActionButton,
  TechDetailsToggle,
  fmtItalianDateTime,
  fmtItalianDate,
  orDash,
  upperOrDash,
  EM_DASH,
} from "./Primitives";

/* ============================================================
 * Defensive accessors (OpenAPI.it response shape may vary)
 * ============================================================ */

const pickSubject = (rd) => {
  if (!rd || typeof rd !== "object") return {};
  const s = rd.soggetto || rd.persona || rd.dati || rd;
  return {
    nome: s.nome || s.first_name,
    cognome: s.cognome || s.last_name,
    cf: s.cf || s.codice_fiscale || s.cf_piva,
    data_nascita: s.data_nascita || s.dataNascita || s.birth_date,
    luogo_nascita:
      s.luogo_nascita ||
      s.luogoNascita ||
      [s.comune_nascita, s.provincia_nascita].filter(Boolean).join(", ") ||
      s.birth_place,
    provincia_nascita: s.provincia_nascita,
    comune_nascita: s.comune_nascita,
    sesso: s.sesso || s.gender,
  };
};

const pickProperties = (rd) => {
  if (!rd || typeof rd !== "object") return [];
  const arr =
    rd.immobili ||
    rd.fabbricati ||
    rd.unita ||
    rd.unita_immobiliari ||
    rd.beni ||
    rd.lista_immobili ||
    [];
  return Array.isArray(arr) ? arr : [];
};

const pickComune = (it) =>
  it?.comune || it?.comune_descrizione || it?.descr_comune || it?.localita || EM_DASH;
const pickProvincia = (it) =>
  it?.provincia || it?.provincia_sigla || it?.sigla_provincia || it?.prov || "";
const pickRegione = (it) => it?.regione || it?.regione_descrizione || "";

const aggregateByComune = (items) => {
  const map = new Map();
  for (const it of items) {
    const comune = pickComune(it);
    const provincia = pickProvincia(it);
    const regione = pickRegione(it);
    const key = `${comune}|${provincia}`;
    if (!map.has(key)) {
      map.set(key, { comune, provincia, regione, count: 0 });
    }
    map.get(key).count += 1;
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
};

/* ============================================================
 * Subject sub-line builder
 * ============================================================ */

const buildEntitySub = (subject) => {
  if (!subject) return null;
  const parts = [];
  const dn = fmtItalianDate(subject.data_nascita);
  if (dn !== EM_DASH) {
    const luogo = subject.luogo_nascita
      ? `${subject.luogo_nascita}${subject.provincia_nascita ? ` (${subject.provincia_nascita})` : ""}`
      : null;
    parts.push(luogo ? `Nato il ${dn} a ${luogo}` : `Nato il ${dn}`);
  } else if (subject.luogo_nascita) {
    parts.push(`Nato a ${subject.luogo_nascita}${subject.provincia_nascita ? ` (${subject.provincia_nascita})` : ""}`);
  }
  if (subject.sesso) parts.push(String(subject.sesso).toUpperCase());
  return parts.length > 0 ? parts.join(" · ") : null;
};

/* ============================================================
 * ResultCatastoPF
 * ============================================================ */

export const ResultCatastoPF = ({ request, onNewSearch }) => {
  const navigate = useNavigate();
  const completedAt = request?.completed_at || request?.created_at;
  const rd = request?.response_data || {};
  const subject = pickSubject(rd);
  const properties = pickProperties(rd);
  const byComune = aggregateByComune(properties);
  const fabbricatiCount = properties.length;
  const provinceCount = new Set(byComune.map((b) => b.provincia || b.comune)).size;
  const fullName =
    [subject.nome, subject.cognome].filter(Boolean).join(" ") || rd.denominazione || EM_DASH;

  const onExportJson = () => {
    try {
      const payload = {
        request_id: request?.id,
        service_id: request?.service_id,
        completed_at: completedAt,
        params: request?.params,
        response_data: rd,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `catasto_pf_${request?.id || "result"}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore export JSON");
    }
  };

  const onPrint = () => window.print();

  const onDownloadPdf = async () => {
    if (!request?.id) return;
    try {
      const token = localStorage.getItem("wattline_token");
      const res = await fetch(`${API}/requests/${request.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error("Errore download PDF"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verifica_catastale_${request.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download PDF"); }
  };

  const handleNewSearch = () => {
    if (onNewSearch) onNewSearch();
    else navigate("/search/catasto");
  };

  const provinceDetail =
    provinceCount === 0 ? null : provinceCount === 1 ? "in 1 provincia" : `in ${provinceCount} province`;

  return (
    <div className="max-w-3xl" data-testid="result-catasto-pf">
      <BackLink to="/history" label="Storico richieste" />

      <HeroStatus
        variant="success"
        title="Ricerca completata"
        description={`Catasto Fabbricati nazionale · ${fmtItalianDateTime(completedAt)}`}
        testId="result-hero"
      />

      <Section>
        <EntityCard
          title="Soggetto identificato"
          entity={fullName}
          meta={subject.cf ? `C.F. ${upperOrDash(subject.cf)}` : null}
          sub={buildEntitySub(subject)}
        />
      </Section>

      <Section>
        <SectionLabel>Patrimonio immobiliare</SectionLabel>
        <StatsRow>
          <StatCard
            label="Fabbricati"
            value={fabbricatiCount}
            detail={provinceDetail}
            testId="stat-fabbricati"
          />
          <StatCard
            label="Terreni"
            value={0}
            detail="Ricerca limitata ai fabbricati"
            muted
            emptyDetail
            testId="stat-terreni"
          />
        </StatsRow>
      </Section>

      {byComune.length > 0 && (
        <Section>
          <SectionLabel>Dettaglio per comune</SectionLabel>
          <DetailList
            testId="detail-list-comuni"
            items={byComune.map((b) => ({
              key: `${b.comune}-${b.provincia}`,
              main: `${b.comune}${b.provincia ? ` (${b.provincia})` : ""}`,
              sub: [b.provincia ? `Provincia di ${b.provincia}` : null, b.regione].filter(Boolean).join(" · ") || null,
              count: `${b.count} ${b.count === 1 ? "fabbricato" : "fabbricati"}`,
            }))}
          />
        </Section>
      )}

      <FooterActions>
        <ActionButton variant="primary" icon={FileText} onClick={onDownloadPdf} testId="action-download-pdf">
          Scarica report PDF
        </ActionButton>
        <ActionButton variant="outline" icon={Plus} onClick={handleNewSearch} testId="action-new-search">
          Nuova ricerca
        </ActionButton>
        <ActionButton variant="outline" icon={Download} onClick={onExportJson} testId="action-export-json">
          Esporta JSON
        </ActionButton>
        <ActionButton variant="outline" icon={Printer} onClick={onPrint} testId="action-print">
          Stampa
        </ActionButton>
      </FooterActions>

      <TechDetailsToggle data={request} />
    </div>
  );
};
